import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import TableActionButton from "@components/buttons/TableActionButton";
import DataPill from "@components/pills/DataPill";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import { institutionApis } from "@services/apis/Institution";
import {
  GetInstitutionalUsersData,
  GetInstitutionalUsersResponse,
} from "@services/models/Institution/GetInstiutionalUsersModel";
import { UpdateInstitutionAccountStatusRequest } from "@services/models/Institution/UpdateInstitutionAccountStatusModel";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConstantFunctions from "../../utils/constants_functions";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import useIsMobile from "../../utils/hooks/useIsMobile";

const CurrentInstitutions = () => {
  const constantFunctions = new ConstantFunctions();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [institutions, setInstitutions] = useState<GetInstitutionalUsersData[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [approvalFilter, setApprovalFilter] = useState<
    "approved" | "pending" | "rejected"
  >("approved");

  const fetchInstitutionalUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response: GetInstitutionalUsersResponse =
        await institutionApis.getInstitutionalUsers({
          approvalStatus: approvalFilter,
        });
      setInstitutions(response.data || []);
    } catch (err: any) {
      setError("Failed to fetch institutional users data");
    } finally {
      setLoading(false);
    }
  }, [approvalFilter]);

  const { handleError, handleSuccess } = useSnackbarMessage();
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const handleApprovalStatus = async (
    id: number,
    status: "approved" | "rejected"
  ) => {
    try {
      setIsButtonLoading(true);
      const updateRequest: UpdateInstitutionAccountStatusRequest = {
        userId: id,
        status: status,
      };

      const data = await institutionApis.updateInstitutionalAccountStatus(
        updateRequest
      );
      handleSuccess({ message: data.msg || "Status updated successfully" });
      fetchInstitutionalUsers();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Updating status failed. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setIsButtonLoading(false);
    }
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "User ID",
        field: "userId",
        flex: 0.5,
        minWidth: 80,
        maxWidth: 90,
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex items-center justify-between h-full gap-1">
              <span
                className="text-[10px] text-text-base font-medium truncate cursor-pointer"
                onClick={() => {
                  navigate(`/institution-management/${data.userId}`, {
                    state: {
                      heading: `Institution Details: ${data.legalEntityName}`,
                    },
                  });
                }}
              >
                {data.userId}
              </span>
              <div onClick={(e) => e.stopPropagation()}>
                <CopyButton
                  copyText={data.userId.toString()}
                  copyMessage="User ID copied!"
                  copyIconSize={10}
                  buttonSize="14px"
                  borderRadius="2px"
                />
              </div>
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Email",
        field: "emailId",
        flex: 1,
        minWidth: 100,
        maxWidth: 180,
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex items-center justify-between h-full gap-1 min-w-0 w-full">
              <div className="flex-1 min-w-0 overflow-hidden">
                <span
                  className="text-[10px] text-text-base font-medium cursor-pointer block"
                  style={{
                    lineHeight: "1.2",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                  }}
                  onClick={() => {
                    navigate(`/institution-management/${data.userId}`, {
                      state: {
                        heading: `Institution Details: ${data.legalEntityName}`,
                      },
                    });
                  }}
                >
                  {data.emailId}
                </span>
              </div>
              <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                <CopyButton
                  copyText={data.emailId}
                  copyMessage="Email copied!"
                  copyIconSize={10}
                  buttonSize="14px"
                  borderRadius="2px"
                />
              </div>
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "Legal Entity Name",
        field: "legalEntityName",
        flex: 2.5,
        minWidth: 280,
        maxWidth: 400,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => (
          <span
            className="text-[10px] text-text-base font-medium block w-full"
            style={{
              lineHeight: "1.2",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            }}
          >
            {params?.data?.legalEntityName || "-"}
          </span>
        ),
      },
      {
        headerName: "Contact Number",
        field: "contactNumber",
        flex: 1.2,
        minWidth: 140,
        maxWidth: 200,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          const { contactCountryCode, contactNumber } = params.data;
          return contactCountryCode && contactNumber
            ? `${contactCountryCode} ${contactNumber}`
            : contactNumber || "";
        },
      },
      {
        headerName: "Approval Status",
        field: "isApproved",
        flex: 1,
        minWidth: 260,
        maxWidth: 340,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          const isApproved = params.data.isApproved === 1;
          return (
            <div className="flex items-center gap-[4px]">
              <DataPill
                value={
                  isApproved
                    ? "approved"
                    : approvalFilter === "rejected"
                      ? "rejected"
                      : "pending"
                }
              />
              {!isApproved && params.data.isApproved !== -1 && (
                <>
                  <TableActionButton
                    variant="danger"
                    icon="cancel"
                    loading={isButtonLoading}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleApprovalStatus(params.data.userId, "rejected");
                    }}
                  >
                    Reject
                  </TableActionButton>
                  <TableActionButton
                    variant="success"
                    icon="check_circle"
                    loading={isButtonLoading}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleApprovalStatus(params.data.userId, "approved");
                    }}
                  >
                    Approve
                  </TableActionButton>
                </>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Created Date",
        field: "createdAt",
        flex: 1,
        minWidth: 120,
        maxWidth: 270,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
        cellRenderer: (params: any) => {
          return constantFunctions.dateFormatInDDMMYY(params.data.createdAt);
        },
      },
    ],
    [constantFunctions, isButtonLoading]
  );

  const onCellClicked = (event: any) => {
    // Only prevent navigation for the isApproved column
    if (event.colDef?.field === "isApproved") return;
    if (event.colDef?.field === "userId") return;
    if (event.colDef?.field === "emailId") return;

    navigate(`/institution-management/${event.data.userId}`, {
      state: { heading: `Institution Details: ${event.data.legalEntityName}` },
    });
  };

  useEffect(() => {
    fetchInstitutionalUsers();
  }, [fetchInstitutionalUsers]);

  return (
    <div
      className="flex flex-col h-screen"
      style={{ height: "calc(100dvh - 120px)" }}
    >
      <div className="flex flex-row items-center justify-start pt-[20px] pb-[12px]">
        <div className="flex flex-row items-center overflow-x-auto overflow-y-hidden">
          <div className="flex justify-start space-x-3 items-center w-full">
            <p className="text-text-base text-[10px] font-bold whitespace-nowrap">
              Filter by:
            </p>
            <div className="flex space-x-3">
              {["approved", "pending", "rejected"].map((status) => (
                <div
                  key={status}
                  onClick={
                    loading
                      ? undefined
                      : () =>
                          setApprovalFilter(
                            status as "approved" | "pending" | "rejected"
                          )
                  }
                  className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] capitalize ${
                    loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                  } ${
                    approvalFilter === status
                      ? "border-border-button-secondary bg-bg-button-secondary"
                      : "border-border-subtle bg-bg-page-body"
                  }`}
                >
                  {status}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col bg-bg-page-body pb-[20px] h-screen">
        {loading ? (
          <AgGridSkeleton
            columnDefs={columnDefs}
            style={{ height: "100%", width: "100%" }}
          />
        ) : (
          <div
            className={
              isMobile
                ? "h-full overflow-x-auto overflow-y-hidden"
                : "h-full overflow-hidden"
            }
          >
            <AgGridReactTable
              ref={gridRef}
              rowData={institutions}
              columnDefs={columnDefs}
              onCellClicked={onCellClicked}
              getRowId={(params) => params.data.userId.toString()}
              error={error}
              rowHeight={isMobile ? 64 : 48}
              style={
                isMobile
                  ? { height: "100%", minWidth: "980px", width: "100%" }
                  : { height: "100%", width: "100%" }
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentInstitutions;
